import type { Todo } from '../types';
import { TaskStatus } from '../types';

export interface InboxCategories {
  unprocessed: Todo[];
  overdue: Todo[];
  aiCreated: Todo[];
}

/** Check if a task has been "processed" — has a horizon set, or is completed/waiting/canceled. */
export function isTaskProcessed(todo: Todo): boolean {
  if (todo.status === TaskStatus.COMPLETED || todo.status === TaskStatus.WAITING || todo.status === TaskStatus.CANCELED) {
    return true;
  }
  if (todo.horizon != null) return true;
  return false;
}

/** Categorize todos into inbox buckets. */
export function categorizeInboxTasks(todos: Todo[]): InboxCategories {
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  const sevenDaysAgo = new Date(now);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const unprocessed: Todo[] = [];
  const overdue: Todo[] = [];
  const aiCreated: Todo[] = [];

  for (const todo of todos) {
    // Skip completed/waiting/canceled
    if (todo.status === TaskStatus.COMPLETED || todo.status === TaskStatus.WAITING || todo.status === TaskStatus.CANCELED) {
      continue;
    }

    // Overdue — deadline in the past
    if (todo.deadline && todo.deadline.slice(0, 10) < todayStr) {
      overdue.push(todo);
      continue;
    }

    // Unprocessed — no horizon set
    if (todo.horizon == null) {
      unprocessed.push(todo);
      continue;
    }

    // Recently AI-created — source is ai_agent, created in last 7 days, no horizon
    if (todo.source === 'ai_agent' && todo.horizon == null) {
      const createdDate = new Date(todo.created_time);
      if (createdDate >= sevenDaysAgo) {
        aiCreated.push(todo);
      }
    }
  }

  return { unprocessed, overdue, aiCreated };
}

/** Get a deduplicated, ordered list of inbox tasks. Order: overdue, unprocessed, AI-created. */
export function getInboxTasks(todos: Todo[]): Todo[] {
  const categories = categorizeInboxTasks(todos);
  const seen = new Set<string>();
  const result: Todo[] = [];

  const addUnique = (tasks: Todo[]) => {
    for (const task of tasks) {
      if (!seen.has(task.id)) {
        seen.add(task.id);
        result.push(task);
      }
    }
  };

  addUnique(categories.overdue);
  addUnique(categories.unprocessed);
  addUnique(categories.aiCreated);

  return result;
}
